import { toast } from 'sonner';
import i18next from 'i18next';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

import { useClassificationStore } from '@/store/classification.store';
import { useTenantStore } from '@/store/tenant.store';

export class ClassificationIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    async fetchClassification(page = 1, pageSize = 10, search = '', sortBy = 'createdAt', sortOrder = 'desc', columnFilters = {}) {
        const store = useClassificationStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);
        store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value && value.length > 0) {
                        acc[`filter_${key}`] = value;
                    }
                    return acc;
                }, {})
            });

            const transformedData = (response.data || []).map((item) => ({
                ...item,
                id: item._id || item.id
            }));
            store.setClassificationItems(transformedData);

            if (response.pagination) {
                store.setPaginationData({
                    currentPage: response.pagination.page || page,
                    totalPages: response.pagination.totalPages || 1,
                    totalItems: response.pagination.total || 0,
                    itemsPerPage: response.pagination.pageSize || pageSize,
                    hasNextPage: response.pagination.page < response.pagination.totalPages,
                    hasPreviousPage: response.pagination.page > 1,
                });
            }

            if (response.uniqueValues) {
                store.setUniqueValues(response.uniqueValues);
            }

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async createClassification(data) {
        const store = useClassificationStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.create({ ...data, tenantId });
            toast.success(i18next.t('classification:messages.createSuccess', { defaultValue: 'Classification created' }));

            if (store.closeForm) store.closeForm();
            await this.fetchClassification();

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            store.setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async updateClassification(id, data) {
        const store = useClassificationStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.update(id, data);
            toast.success(i18next.t('classification:messages.updateSuccess', { defaultValue: 'Classification updated' }));

            if (store.closeForm) store.closeForm();
            await this.fetchClassification();

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            store.setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async deleteClassification(id) {
        const store = useClassificationStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            await this.service.delete(id);
            toast.success(i18next.t('classification:messages.deleteSuccess', { defaultValue: 'Classification deleted' }));

            if (store.closeDeleteDialog) store.closeDeleteDialog();
            await this.fetchClassification();

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async bulkDeleteClassification(ids) {
        const store = useClassificationStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            await this.service.bulkDelete(ids);
            toast.success(i18next.t('classification:messages.bulkDeleteSuccess', {
                count: ids.length,
                defaultValue: `${ids.length} classifications deleted`
            }));
            await this.fetchClassification();

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    _getErrorMessage(error) {
        if (error.isValidationError?.()) {
            return i18next.t('common:messages.validationError');
        }
        if (error.isNotFound?.()) {
            return i18next.t('common:messages.notFoundError');
        }
        if (error.isAuthError?.()) {
            return i18next.t('common:messages.authError');
        }
        return error.message || i18next.t('common:messages.unexpectedError');
    }
}
